import { useEffect, useState } from "react";
import { Iridescence } from "./Iridescence";
import { useAudioLevel } from "../hooks/useAudioLevel";

interface TalkingOrbProps {
  /** Whether the orb is active (animating) */
  isActive?: boolean;
  /** Size in pixels */
  size?: number;
  /** Color as RGB values (0-1 range) */
  color?: [number, number, number];
  /** Audio track for visualization */
  audioTrack?: MediaStreamTrack | null;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Animated orb that responds to audio levels
 */
export function TalkingOrb({
  isActive = true,
  size = 200,
  color = [0.3, 0.6, 1],
  audioTrack = null,
  className,
}: TalkingOrbProps) {
  const { levelRef } = useAudioLevel(audioTrack);
  const [level, setLevel] = useState(0);

  // Smooth animation of level changes
  useEffect(() => {
    if (!isActive) {
      setLevel(0);
      return;
    }

    let raf = 0;
    const update = () => {
      setLevel((prev) => prev + (levelRef.current - prev) * 0.1);
      raf = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(raf);
  }, [levelRef, isActive]);

  const amplitude = 0.05 + level * 0.4;
  const speed = level > 0.1 ? 0.7 : 0.1;
  const scale = 1 + level * 0.7;
  const glowOpacity = 0.1 + level * 0.7;

  return (
    <div
      className={`relative ${className || ""}`}
      style={{ width: size, height: size }}
    >
      {/* Glow effect - uses dynamic opacity based on audio level */}
      <div
        className="absolute inset-0 rounded-full bg-blue-500/50 blur-[130px] transition-opacity duration-300 ease-out"
        style={{ opacity: glowOpacity }}
      />
      {/* Orb with shader - uses dynamic scale based on audio level */}
      <div
        className="relative h-full w-full rounded-full overflow-hidden shadow-[0_0_90px_rgba(58,108,255,0.45)] transition-transform duration-300 ease-out"
        style={{ transform: `scale(${scale})` }}
      >
        <Iridescence amplitude={amplitude} speed={speed} color={color} />
      </div>
    </div>
  );
}
